import React, { useEffect, useState, useRef } from "react";

import {
  Box,
  Card,
  Stack,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import ShareIcon from "@mui/icons-material/Share";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import InsightsIcon from "@mui/icons-material/Insights";
import { ClosedCaption } from "@mui/icons-material";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import styled from "styled-components";

import SidebarView, { SidebarRef } from "../common/Sidebar";
import { episodeService } from "./services";
import ShareEpisodeDialog from "./components/ShareEpisodeDialog";
import { WebSocketEventName } from "../common/enums/WebSocketEventName";
import { useWebSocket } from "../../hooks/useWebSocket";
import Insights from "./components/Insights";
import Preview from "./components/Preview";
import ButtonView from "../common/Button";
import Breadcrumbs from "../common/Breadcrumbs";
import "../../episode-mobile.css";
import Headerbar from "../common/Headerbar";

const StyledTab = styled(Tab)(
  () => `
  && {
    font-size: 14px !important;
    min-height: 48px;
    padding: 0px 20px;
  }
  
  &&.MuiTab-root {
    font-size: 11px !important;
  }
  
  @media (max-width: 600px) {
    && {
      font-size: 10px !important;
      padding: 0px 8px;
      min-height: 40px;
    }
    
    &&.MuiTab-root {
      font-size: 10px !important;
    }
  }
`,
);

export default function Episode() {
  const { id: episodeId } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const sidebarRef = useRef<SidebarRef>(null);
  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const [showShareEpisodeModal, setShowShareEpisodeModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState<"preview" | "insights">(
    "preview",
  );

  const [tabValue, setTabValue] = React.useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const socket = useWebSocket();

  const { data: insights, refetch } = useQuery({
    queryKey: ["insights", episodeId],
    queryFn: () => episodeService.fetchInsights(episodeId),
  });

  const { data: episode } = useQuery({
    queryKey: ["getEpisodeById", episodeId],
    queryFn: () => episodeService.getEpisodeById(episodeId),
  });

  const handleOpenShareEpisodeModal = () => {
    setShowShareEpisodeModal(true);
  };

  const handleCloseShareEpisodeModal = () => {
    setShowShareEpisodeModal(false);
  };

  useEffect(() => {
    if (socket) {
      socket.on(WebSocketEventName.EPISODE_INSIGHTS_GENERATED, async () => {
        await refetch();
      });
    }

    return () => {
      if (socket) {
        socket.off(WebSocketEventName.EPISODE_INSIGHTS_GENERATED);
      }
    };
  }, [socket]);

  // Prevent page scrolling
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalStyle;
      document.documentElement.style.overflow = originalStyle;
    };
  }, []);

  // Calculate responsive values
  let cardPaddingX = 4;
  let cardPaddingY = 6;
  let cardMarginX = 4;
  let cardMarginTop = 4;

  if (isMobile) {
    cardPaddingX = 2;
    cardPaddingY = 3;
    cardMarginX = 2;
    cardMarginTop = 2;
  } else if (isTablet) {
    cardPaddingX = 3;
    cardPaddingY = 4;
    cardMarginX = 3;
    cardMarginTop = 3;
  }

  // Check if episode is audio file type
  const isAudioFile = episode?.upload?.mime_type?.startsWith("audio/");

  return (
    <>
      {showShareEpisodeModal && episode && (
        <ShareEpisodeDialog
          episode={episode}
          handleClose={handleCloseShareEpisodeModal}
        />
      )}
      <SidebarView
        ref={sidebarRef}
        pageName={`Episode - ${episode?.name || ""}`}
      />
      <Box
        component="main"
        className="episode-main-content"
        sx={{
          backgroundColor:
            theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: "100%",
          [theme.breakpoints.down("sm")]: {
            marginLeft: 0,
          },
        }}
      >
        <Headerbar
          pageName={`Episode - ${episode?.name || ""}`}
          onMobileMenuClick={handleMobileMenuToggle}
        />
        <Breadcrumbs
          items={[
            {
              label: "Episodes",
              path: "/episodes",
              icon: <PlaylistPlayIcon sx={{ fontSize: 18 }} />,
            },
            {
              label: episode?.name || "Episode",
              path: `/episodes/${episodeId}`,
            },
          ]}
          showHome={false}
        />
        <Card
          component={Stack}
          spacing={0}
          direction="column"
          className="episode-content"
          sx={{
            position: "relative",
            height: "calc(100vh - 64px - 64px)", // Full height minus AppBar minus marginTop
            px: cardPaddingX,
            py: cardPaddingY,
            borderRadius: 2,
            marginX: cardMarginX,
            marginTop: cardMarginTop,
            marginBottom: 0,
            [theme.breakpoints.down("sm")]: {
              height: "calc(100vh - 56px - 56px)", // Full height minus mobile AppBar minus marginTop
              marginBottom: 0,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              borderBottom: 1,
              borderColor: "divider",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              position: "relative",
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="episode tabs"
              sx={{
                flexGrow: 1,
                [theme.breakpoints.down("sm")]: {
                  width: "100%",
                  "& .MuiTabs-flexContainer": {
                    justifyContent: "space-around",
                  },
                  "& .MuiTab-root": {
                    fontSize: "10px",
                    padding: "6px 8px",
                    minHeight: "40px",
                  },
                },
              }}
            >
              <StyledTab
                label="Preview"
                icon={<ClosedCaption />}
                iconPosition="start"
                onClick={() => setSelectedTab("preview")}
                sx={{
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "10px",
                    padding: "0px 8px",
                    minHeight: "40px",
                  },
                }}
              />
              <StyledTab
                label="Insights"
                icon={<InsightsIcon />}
                iconPosition="start"
                onClick={() => setSelectedTab("insights")}
                sx={{
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "10px",
                    padding: "0px 8px",
                    minHeight: "40px",
                  },
                }}
              />
            </Tabs>

            {/* Action buttons */}
            <Box
              className="episode-action-buttons"
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: 1,
                mt: isMobile ? 2 : 0,
                ml: isMobile ? 0 : 2,
                width: isMobile ? "100%" : "auto",
                position: isMobile ? "static" : "absolute",
                top: isMobile ? "auto" : 0,
                right: isMobile ? "auto" : 0,
                zIndex: isMobile ? "auto" : 99,
              }}
            >
              {isAudioFile ? null : (
                <ButtonView
                  sx={{
                    fontSize: "10px",
                    width: isMobile ? "100%" : "auto",
                    height: isMobile ? "36px" : "auto",
                  }}
                  type="button"
                  variant="contained"
                  size="small"
                  label="Shorts"
                  startIcon={<OndemandVideoIcon />}
                  href={`/episodes/${episodeId}/videos`}
                />
              )}
              <ButtonView
                sx={{
                  fontSize: "10px",
                  width: isMobile ? "100%" : "auto",
                  height: isMobile ? "36px" : "auto",
                }}
                type="button"
                variant="contained"
                size="small"
                label="Share Episode"
                startIcon={<ShareIcon />}
                disabled
                handleOnClick={handleOpenShareEpisodeModal}
              />
            </Box>
          </Box>

          <Box
            sx={{
              mt: isMobile ? 2 : 4,
              px: 0,
              height: "calc(100% - 80px)", // Full height minus header area
              overflow: "hidden", // Prevent overflow
            }}
          >
            {selectedTab === "preview" && (
              <Box
                sx={{
                  height: "100%", // Full height of parent
                  [theme.breakpoints.down("sm")]: {
                    px: 0,
                  },
                }}
              >
                {episodeId && <Preview episode={episode} />}
              </Box>
            )}
            {selectedTab === "insights" && (
              <Box
                sx={{
                  height: "100%", // Full height of parent
                  overflowY: "auto", // Allow scrolling for insights
                  [theme.breakpoints.down("sm")]: {
                    px: 0,
                  },
                }}
              >
                {episodeId && (
                  <Insights
                    insights={insights}
                    refetch={refetch}
                    episodeId={episodeId}
                  />
                )}
              </Box>
            )}
          </Box>
        </Card>
      </Box>
    </>
  );
}
